// Tubular Mechanics — burst (Barlow), simple axial-with-buoyancy, plus advanced placeholders.
import { round, num, positive, comingSoonFn } from "./_common";
// eslint-disable-next-line no-unused-vars
const _imports_ok = { round, num, positive, comingSoonFn };

// Barlow's burst (API 5C3 simplified, with 0.875 wall tolerance factor).
// P_burst = (2 × Yp × t / D) × 0.875
export function pipeBurstBarlow({ yieldStrength, wallThickness, od }) {
  const Yp = positive(num(yieldStrength, "Yield Strength"), "Yield Strength");
  const t = positive(num(wallThickness, "Wall Thickness"), "Wall Thickness");
  const D = positive(num(od, "Outside Diameter"), "Outside Diameter");
  const p = (2 * Yp * t / D) * 0.875;
  return {
    result: round(p, 0),
    unit: "psi (Barlow, w/ 0.875)",
    formula: "P_burst ≈ (2 × Yp × t / D) × 0.875   (API minimum-wall tolerance)",
    steps: [
      `Yp = ${Yp} psi,  t = ${t} in,  D = ${D} in`,
      `P_burst = (2 × ${Yp} × ${t} / ${D}) × 0.875`,
      `P_burst = ${round(p, 0)} psi`,
    ],
    inputs: { "Yp (psi)": Yp, "Wall t (in)": t, "OD (in)": D },
    worksheetWarning: true,
  };
}

// Axial load along a string section incorporating buoyancy.
// F_axial = W_air × L × BF   (per uniform section, in air-weight × length × buoyancy)
export function axialWithBuoyancy({ weightLbFt, length, mw }) {
  const W = positive(num(weightLbFt, "Weight"), "Weight");
  const L = positive(num(length, "Length"), "Length");
  const M = positive(num(mw, "Mud Weight"), "Mud Weight");
  const BF = 1 - M / 65.5;
  const F = W * L * BF;
  return {
    result: round(F, 0),
    unit: "lbf (axial, buoyed)",
    formula: "F_axial = W × L × BF   (BF = 1 − MW/65.5)",
    steps: [
      `BF = 1 − ${M}/65.5 = ${round(BF, 4)}`,
      `F = ${W} × ${L} × ${round(BF, 4)} = ${round(F, 0)} lbf`,
    ],
    inputs: { "W (lb/ft)": W, "L (ft)": L, "MW (ppg)": M },
    extras: { "BF": round(BF, 4) },
  };
}

// =========================================================================
// PIPE COLLAPSE — Utilization Worksheet (user-entered rating).
// Does NOT compute API 5C3 four-regime collapse from grade/geometry. Instead it
// accepts a published collapse rating (from API/manufacturer tables) plus the
// applied external/internal pressures and a safety factor, then reports the
// utilization vs. the allowable (rated / SF). Axial-tension derating is not
// included — see Triaxial Summary for combined-load review.
// =========================================================================
export function pipeCollapseWorksheet({ collapseRating, externalPressure, internalPressure, safetyFactor }) {
  const Pc = positive(num(collapseRating, "Collapse Rating"), "Collapse Rating");
  const Pe = positive(num(externalPressure, "External Pressure"), "External Pressure");
  const Pi = num(internalPressure ?? 0, "Internal Pressure"); if (Pi < 0) throw new Error("Internal Pressure must be ≥ 0");
  const SF = positive(num(safetyFactor ?? 1, "Safety Factor"), "Safety Factor");
  const net = Pe - Pi;                       // collapse-loading differential
  const allowable = Pc / SF;
  const util = net <= 0 ? 0 : (net / allowable) * 100;
  const status = net <= 0
    ? "No net collapse load (internal ≥ external)"
    : util <= 100
      ? `PASS — net collapse load ${round(net, 0)} psi within allowable ${round(allowable, 0)} psi`
      : `FAIL — net collapse load ${round(net, 0)} psi EXCEEDS allowable ${round(allowable, 0)} psi`;
  return {
    result: round(util, 1),
    unit: "%",
    formula: "Net = Pe − Pi ;  Allowable = Pc / SF ;  Utilization = Net / Allowable × 100",
    steps: [
      `Net collapse load = ${Pe} − ${Pi} = ${round(net, 0)} psi`,
      `Allowable = ${Pc} / ${SF} = ${round(allowable, 0)} psi`,
      `Utilization = ${round(net, 0)} / ${round(allowable, 0)} × 100 = ${round(util, 1)} %`,
      `Status: ${status}`,
    ],
    inputs: { "Rating Pc (psi)": Pc, "External (psi)": Pe, "Internal (psi)": Pi, "Safety Factor": SF },
    extras: {
      "Net (psi)": round(net, 0),
      "Allowable (psi)": round(allowable, 0),
      "Status": status,
      "Note": "Requires Engineering Verification — API 5C3 collapse depends on grade, D/t, axial derating, wear, corrosion, ovality, temperature, fatigue, and connection limits.",
    },
    worksheetWarning: true,
  };
}
export const pipeCollapseAPI = pipeCollapseWorksheet;

// =========================================================================
// PIPE TENSILE CAPACITY — Pipe Body Yield Estimate (simplified).
// Pa = Yp × As ,  As = π/4 · (OD² − ID²)
// This is the pipe-body yield only. It is NOT the API joint-strength or
// connection-rating value. Always confirm against API tensile rating tables
// for the specific grade, weight, and connection.
// =========================================================================
export function pipeTensileCapacityWorksheet({ yieldStrength, od, id, designFactor }) {
  const Yp = positive(num(yieldStrength, "Yield Strength"), "Yield Strength");
  const D  = positive(num(od, "OD"), "OD");
  const I  = num(id, "ID"); if (I < 0 || I >= D) throw new Error("ID must satisfy 0 ≤ ID < OD");
  const DF = positive(num(designFactor ?? 1, "Design Factor"), "Design Factor");
  if (DF > 1) throw new Error("Design Factor must be ≤ 1 (e.g. 0.9)");
  const As = (Math.PI / 4) * (D * D - I * I);
  const Pa = Yp * As;
  const allowable = Pa * DF;
  return {
    result: round(Pa, 0),
    unit: "lbf",
    formula: "Pa = Yp × As ,  As = π/4·(OD² − ID²)   (pipe body yield only)",
    steps: [
      `As = π/4 × (${D}² − ${I}²) = ${round(As, 3)} in²`,
      `Pa (pipe body yield) = ${Yp} × ${round(As, 3)} = ${round(Pa, 0)} lbf`,
      `Allowable = Pa × Design Factor = ${round(Pa, 0)} × ${DF} = ${round(allowable, 0)} lbf`,
    ],
    inputs: { "Yp (psi)": Yp, "OD (in)": D, "ID (in)": I, "Design Factor": DF },
    extras: {
      "As (in²)": round(As, 3),
      "Pipe Body Yield (lbf)": round(Pa, 0),
      "Allowable @ DF (lbf)": round(allowable, 0),
      "Note": "Pipe body yield estimate only — confirm joint/connection tensile rating against API/manufacturer tables.",
    },
    worksheetWarning: true,
  };
}
export const pipeTensileCapacity = pipeTensileCapacityWorksheet;

// =========================================================================
// TRIAXIAL STRESS SUMMARY — utilization worksheet (uses user-entered ratings).
// Accepts published burst / collapse / tensile ratings plus the applied
// internal pressure, external pressure, and axial load. Computes per-mode
// utilization vs. allowable (rating / safety factor) and a pass/fail flag.
// Does NOT perform Von Mises triaxial stress, biaxial derating, connection
// limits, wear, corrosion, fatigue, or temperature derating.
// =========================================================================
export function triaxialUtilizationSummary({
  burstRating, collapseRating, tensileRating,
  internalPressure, externalPressure, axialLoad,
  safetyFactor,
}) {
  const Pb = positive(num(burstRating, "Burst Rating"), "Burst Rating");
  const Pc = positive(num(collapseRating, "Collapse Rating"), "Collapse Rating");
  const Tr = positive(num(tensileRating, "Tensile Rating"), "Tensile Rating");
  const Pi = num(internalPressure ?? 0, "Internal Pressure"); if (Pi < 0) throw new Error("Internal Pressure must be ≥ 0");
  const Pe = num(externalPressure ?? 0, "External Pressure"); if (Pe < 0) throw new Error("External Pressure must be ≥ 0");
  const Fa = num(axialLoad ?? 0, "Axial Load");
  const SF = positive(num(safetyFactor ?? 1, "Safety Factor"), "Safety Factor");

  const burstNet    = Pi - Pe;
  const collapseNet = Pe - Pi;
  const allowBurst    = Pb / SF;
  const allowCollapse = Pc / SF;
  const allowTension  = Tr / SF;

  const utilBurst    = burstNet    > 0 ? (burstNet    / allowBurst)    * 100 : 0;
  const utilCollapse = collapseNet > 0 ? (collapseNet / allowCollapse) * 100 : 0;
  const utilAxial    = Math.abs(Fa) > 0 ? (Math.abs(Fa) / allowTension) * 100 : 0;
  const maxUtil = Math.max(utilBurst, utilCollapse, utilAxial);

  const mode = maxUtil === utilBurst ? "Burst" : maxUtil === utilCollapse ? "Collapse" : "Axial";
  const verdict = maxUtil <= 100
    ? `PASS — controlling mode: ${mode} at ${round(maxUtil, 1)} % of allowable`
    : `FAIL — ${mode} utilization ${round(maxUtil, 1)} % EXCEEDS 100 % allowable`;

  return {
    result: round(maxUtil, 1),
    unit: "%",
    formula: "util_mode = applied_mode / (rating_mode / SF) × 100 ;  controlling = max(util)",
    steps: [
      `Burst:    net = ${Pi} − ${Pe} = ${round(burstNet, 0)} psi ;  allow = ${Pb}/${SF} = ${round(allowBurst, 0)} psi ;  util = ${round(utilBurst, 1)} %`,
      `Collapse: net = ${Pe} − ${Pi} = ${round(collapseNet, 0)} psi ;  allow = ${Pc}/${SF} = ${round(allowCollapse, 0)} psi ;  util = ${round(utilCollapse, 1)} %`,
      `Axial:    |F| = ${Math.abs(Fa)} lbf ;  allow = ${Tr}/${SF} = ${round(allowTension, 0)} lbf ;  util = ${round(utilAxial, 1)} %`,
      `Controlling mode: ${mode}  ·  ${verdict}`,
    ],
    inputs: {
      "Burst Pb (psi)": Pb, "Collapse Pc (psi)": Pc, "Tensile Tr (lbf)": Tr,
      "Internal Pi (psi)": Pi, "External Pe (psi)": Pe, "Axial Fa (lbf)": Fa,
      "Safety Factor": SF,
    },
    extras: {
      "Burst util (%)": round(utilBurst, 1),
      "Collapse util (%)": round(utilCollapse, 1),
      "Axial util (%)": round(utilAxial, 1),
      "Controlling mode": mode,
      "Status": verdict,
      "Note": "Requires Engineering Verification — not a substitute for API/manufacturer triaxial design, biaxial derating, connection limits, wear, corrosion, temperature, fatigue, or company approval.",
    },
    worksheetWarning: true,
  };
}
export const triaxialSummary = triaxialUtilizationSummary;
